import { Pencil, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import type {
    BranchSalaryHeadType,
    IBranchSalaryHead,
} from "../../interface/branchSalaryHead.types";

interface BranchSalaryHeadSectionProps {
    type: BranchSalaryHeadType;
    items: IBranchSalaryHead[];
    isLoading: boolean;
    isSubmitting: boolean;
    onEdit: (item: IBranchSalaryHead) => void;
    onDelete: (id: string) => void;
}

const sectionConfig = {
    earning: {
        title: "Earnings",
        emptyText: "No earning heads found",
        loadingText: "Loading earnings...",
        Icon: TrendingUp,
        borderClass: "border-emerald-200/80",
        backgroundClass: "bg-gradient-to-b from-emerald-50/60 to-emerald-50/10",
        iconClass: "bg-emerald-100 text-emerald-700",
        headingClass: "text-emerald-950",
        badgeClass: "bg-emerald-100 text-emerald-800 border-emerald-200",
        itemBorderClass: "border-emerald-100/90 hover:border-emerald-300/60",
        textHoverClass: "group-hover:text-emerald-900",
        emptyBorderClass: "border-emerald-200",
    },
    deduction: {
        title: "Deductions",
        emptyText: "No deduction heads found",
        loadingText: "Loading deductions...",
        Icon: TrendingDown,
        borderClass: "border-rose-200/80",
        backgroundClass: "bg-gradient-to-b from-rose-50/60 to-rose-50/10",
        iconClass: "bg-rose-100 text-rose-700",
        headingClass: "text-rose-950",
        badgeClass: "bg-rose-100 text-rose-800 border-rose-200",
        itemBorderClass: "border-rose-100/90 hover:border-rose-300/60",
        textHoverClass: "group-hover:text-rose-900",
        emptyBorderClass: "border-rose-200",
    },
};

const BranchSalaryHeadSection: React.FC<BranchSalaryHeadSectionProps> = ({
    type,
    items,
    isLoading,
    isSubmitting,
    onEdit,
    onDelete,
}) => {
    const config = sectionConfig[type];
    const Icon = config.Icon;

    return (
        <div className={`border ${config.borderClass} rounded-2xl p-5 ${config.backgroundClass} shadow-sm flex flex-col justify-between`}>
            <div>
                <div className={`flex items-center justify-between mb-5 pb-3 border-b ${type === "earning" ? "border-emerald-200/60" : "border-rose-200/60"}`}>
                    <div className="flex items-center gap-2.5">
                        <div className={`p-2.5 rounded-xl ${config.iconClass} shadow-inner`}>
                            <Icon className="h-5 w-5" />
                        </div>
                        <h4 className={`font-bold text-lg ${config.headingClass}`}>{config.title}</h4>
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${config.badgeClass}`}>
                        {items.length} {items.length === 1 ? "Head" : "Heads"}
                    </span>
                </div>

                <div className="flex flex-col gap-3">
                    {isLoading ? (
                        <div className={`border ${config.itemBorderClass} rounded-xl px-4 py-3.5 bg-white text-sm font-semibold text-slate-500`}>
                            {config.loadingText}
                        </div>
                    ) : items.length > 0 ? (
                        items?.map((item) => (
                            <div key={item?.id} className={`border ${config.itemBorderClass} rounded-xl px-4 py-3.5 flex justify-between items-center bg-white shadow-sm hover:shadow-md transition-all group`}>
                                <div>
                                    <span className={`font-semibold text-slate-700 ${config.textHoverClass} transition-colors`}>
                                        {item?.salaryHead?.name || "-"}
                                    </span>
                                    {/* <p className="text-xs font-semibold text-slate-500 mt-1">Amount: {item.amount || "0"}</p> */}
                                </div>

                                {!item?.salaryHead?.isBase && (
                                    <div className="flex items-center gap-2">
                                        <Button type="button" variant="outline" size="icon" onClick={() => onEdit(item)} disabled={isSubmitting}>
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button type="button" variant="destructive" size="icon" onClick={() => onDelete(item.id)} disabled={isSubmitting}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className={`border border-dashed ${config.emptyBorderClass} rounded-xl px-4 py-3.5 bg-white text-sm font-semibold text-slate-500`}>
                            {config?.emptyText}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BranchSalaryHeadSection;
